import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, CircularProgress } from '@mui/material'

import './CButton.css'
import Icon from './Icon'

const CButton = ({
    label = '',
    onClick,
    disabled = false,
    type = 'button',
    autoFocus = false,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    fullWidth = false,
    leftIcon = '',
    rightIcon = '',
    iconType = 'far',
    showLoader = false,
    loading = false,
    className = '',
    colorTheme = 'primary',
    borderRadius = '6px',
    alignment = 'center',
    children,
    ...props
}) => {
    const [buttonStates, setButtonStates] = useState({
        className,
        label,
        disabled,
        variant,
        color,
        size,
        fullWidth,
        leftIcon,
        rightIcon,
        iconType,
        showLoader,
        loading,
        colorTheme,
        borderRadius,
        type,
        autoFocus,
        alignment,
    })

    useEffect(() => {
        setButtonStates({
            className,
            label,
            disabled,
            variant,
            color,
            size,
            fullWidth,
            leftIcon,
            rightIcon,
            iconType,
            showLoader,
            loading,
            colorTheme,
            borderRadius,
            type,
            autoFocus,
            alignment,
        })
    }, [className, label, disabled, variant, color, size, fullWidth, leftIcon, rightIcon, iconType, showLoader, loading, colorTheme, borderRadius, type, autoFocus, alignment])

    const handleClick = (event) => {
        if (!buttonStates.disabled && !buttonStates.loading && onClick) {
            onClick(event)
        }
    }

    const isLoading = buttonStates.showLoader || buttonStates.loading

    return (
        <div className={`c-button color-theme-${buttonStates.colorTheme} ${buttonStates.fullWidth ? 'full-width' : ''}`}>
            <Button
                className={`c-button-element ${buttonStates.className} text-align-${buttonStates.alignment}`}
                onClick={handleClick}
                disabled={buttonStates.disabled || isLoading}
                variant={buttonStates.variant}
                color={buttonStates.color}
                size={buttonStates.size}
                fullWidth={buttonStates.fullWidth}
                type={buttonStates.type}
                autoFocus={buttonStates.autoFocus}
                style={{ borderRadius: buttonStates.borderRadius }}
                startIcon={
                    buttonStates.leftIcon && !isLoading ? (
                        <Icon name={buttonStates.leftIcon} className="c-button-icon c-button-icon-left" />
                    ) : null
                }
                endIcon={
                    buttonStates.rightIcon && !isLoading ? (
                        <Icon name={buttonStates.rightIcon} className="c-button-icon c-button-icon-right" />
                    ) : null
                }
            >
                {isLoading ? (
                    <div className="c-button-loading">
                        <CircularProgress
                            size={20}
                            thickness={4}
                            color="inherit"
                            className="c-button-spinner"
                        />
                        {buttonStates.label && (
                            <span className="c-button-loading-text">
                                {buttonStates.label}
                            </span>
                        )}
                    </div>
                ) : (
                    buttonStates.label || children
                )}
            </Button>
        </div>
    )
}

CButton.propTypes = {
    // Basic button props
    label: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    autoFocus: PropTypes.bool,

    // Material-UI Button props
    variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
    color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    fullWidth: PropTypes.bool,

    // Icon props
    leftIcon: PropTypes.string,
    rightIcon: PropTypes.string,
    iconType: PropTypes.oneOf(['far', 'fas', 'fal', 'fab']),

    // Loading props
    showLoader: PropTypes.bool,
    loading: PropTypes.bool,

    // Styling props
    className: PropTypes.string,
    colorTheme: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),
    borderRadius: PropTypes.string,
    alignment: PropTypes.oneOf(['left', 'center', 'right']),

    // Children for custom content
    children: PropTypes.node,
}

export default CButton